import { Box, Flex, Loading, Text } from '@workwhile/ui'
import { ShiftRowSchema } from 'pages/Shifts/BulkCreateShifts/bulkCreateShiftsSchema'
import { useBulkCreateShiftsContextValue } from 'pages/Shifts/BulkCreateShifts/context/BulkCreateShiftsProvider'
import React from 'react'
import styles from './ReviewTable.module.css'
import { toFormattedAddress } from 'shared/utils/toFormattedAddress'
import { useCompanyDetailsByIdQuery } from 'queries/company'
import { differenceInMinutes, format } from 'date-fns'
import { getLongDuration } from 'lib/time'

const LocationStatusCell = ({ rowId }: { rowId: string }) => {
  const {
    state: { checkLocationsState },
  } = useBulkCreateShiftsContextValue()

  if (checkLocationsState.isPending) {
    return <Loading size={16} />
  }

  const result = checkLocationsState?.result?.locations?.[rowId]
  const isNewLocation = !result?.existingLocationId

  return (
    <Text color={isNewLocation ? 'success' : 'info'}>
      {isNewLocation ? 'Yes' : 'No'}
    </Text>
  )
}

const ReviewRow = ({
  shift,
  index,
}: {
  shift: ShiftRowSchema
  index: number
}) => {
  const {
    state: { companies },
  } = useBulkCreateShiftsContextValue()
  const { data: companyDetail } = useCompanyDetailsByIdQuery(shift.companyId)

  const company = companies.find((company) => company.id === shift.companyId)
  const position = companyDetail?.positions?.find(
    (position) => position.id === shift.positionId
  )

  const formattedAddress = toFormattedAddress({
    street: shift.locationStreet,
    unit: shift.locationUnit,
    city: shift.locationCity,
    state: shift.locationState,
    zip: shift.locationZip,
  })

  const formattedShiftStartDateTime = format(
    shift.shiftStartDate,
    'MMM dd, yyyy h:mma'
  )

  const formattedShiftEndDateTime = format(
    shift.shiftEndDate,
    'MMM dd, yyyy h:mma'
  )

  const shiftDuration = differenceInMinutes(
    shift.shiftEndDate,
    shift.shiftStartDate
  )
  const formattedShiftDuration = getLongDuration(shiftDuration)

  const formattedLunchLength =
    shift.shiftLunchLength > 0 ? `${shift.shiftLunchLength} mins` : 'no lunch'

  const formattedShiftPay = shift.shiftPayRate
    ? `$${shift.shiftPayRate}/hr`
    : `$${shift.shiftPayLumpSum}`

  const formattedShiftBundleGroup = shift.shiftBundleGroup || 'no bundle'

  const formattedShiftSupervisor = shift.shiftSupervisorName
    ? `${shift.shiftSupervisorName} (${shift.shiftSupervisorPhone})`
    : 'no supervisor'

  const formattedWorkerIds = shift.workerIds?.join(', ')

  return (
    <tr>
      <td align="center">{index + 1}</td>
      <td>{company?.name ?? shift.companyId}</td>
      <td>{shift.locationName}</td>
      <td>{shift.locationArrivalInstructions}</td>
      <td>{formattedAddress}</td>
      <td align="center">
        <LocationStatusCell rowId={shift.rowId} />
      </td>
      <td>{position?.name ?? shift.positionId}</td>
      <td>{formattedShiftStartDateTime}</td>
      <td>
        {formattedShiftEndDateTime} ({formattedShiftDuration})
      </td>
      <td>{shift.shiftWorkersNeeded}</td>
      <td>{formattedLunchLength}</td>
      <td>{formattedShiftPay}</td>
      <td>{formattedShiftBundleGroup}</td>
      <td>{formattedShiftSupervisor}</td>
      <td>{formattedWorkerIds}</td>
    </tr>
  )
}

export const ReviewTable = () => {
  const {
    state: { formState },
  } = useBulkCreateShiftsContextValue()

  return (
    <Flex
      flexDirection="column"
      gap={3}
      align="center"
      justifyContent="flex-start"
      height="100%"
      minHeight="auto"
    >
      <Box width="100%" overflow="auto" pb={3}>
        <table className={styles.shiftsTable}>
          <thead>
            <tr>
              <th align="center" className={styles.rowNumberColumn}>
                #
              </th>
              <th align="center">
                <Text>Company</Text>
              </th>
              <th align="center">
                <Text>Location Name</Text>
              </th>
              <th align="center">
                <Text>Location Arrival Instructions</Text>
              </th>
              <th align="center">
                <Text>Location Address</Text>
              </th>
              <th align="center">
                <Text>New Location?</Text>
              </th>
              <th align="center">
                <Text>Position</Text>
              </th>
              <th align="center">
                <Text>Starts At</Text>
              </th>
              <th align="center">
                <Text>Ends At</Text>
              </th>
              <th align="center">
                <Text>Workers Needed</Text>
              </th>
              <th align="center">
                <Text>Lunch Length</Text>
              </th>
              <th align="center">
                <Text>Pay</Text>
              </th>
              <th align="center">
                <Text>Bundle Group</Text>
              </th>
              <th align="center">
                <Text>Supervisor</Text>
              </th>
              <th align="center">
                <Text>Worker IDs</Text>
              </th>
            </tr>
          </thead>
          <tbody>
            {formState?.shifts?.map((shift, index) => (
              <ReviewRow
                key={`shift-${shift.rowId}`}
                index={index}
                shift={shift}
              />
            ))}
          </tbody>
        </table>
      </Box>
    </Flex>
  )
}
